<template>
  <header class="header">
    <nav class="topnav d-none d-md-block">
      <ul class="container topnav__ul d-flex align-items-center justify-content-end bold">
        <li class="topnav__li">
          <app-session-button
            class="session"
            @click="show_mobile_menu = false"
            @close="show_mobile_menu = false"
          />
        </li>

        <template v-if="topnav.navigation?.length">
          <li
            v-for="({ link }, index) in topnav.navigation"
            :key="`topnav_${index}`"
            class="topnav__li"
          >
            <app-link
              :link="link"
              class="topnav__a link--naked link__color--black-prim bold"
            />
          </li>
        </template>

        <li class="topnav__li topnav__li--switcher">
          <language-switcher class="language-switcher">
            <template #option="{ option }">
              {{ option.value.toUpperCase() }}
            </template>
          </language-switcher>
        </li>
      </ul>
    </nav>

    <div class="container header__container">
      <app-logo :logo="logo.image" />

      <nav class="navigation">
        <ul
          class="navigation__ul d-none d-md-flex align--center"
          v-if="navigation.length"
        >
          <li
            v-for="({ link }, index) in navigation"
            :key="`navigation_${index}`"
            class="navigation__li"
          >
            <app-link
              :link="link"
              class="navigation__a link--naked link__color--black-prim bold"
            />
          </li>
        </ul>

        <app-link
          :link="cta"
          class="bold btn btn__color--prim btn__size--classic cta bold"
        />

        <app-burger-menu
          class="burger d-md-none"
          :state="show_mobile_menu"
          @toggle="toggleMobileMenu"
        />
      </nav>
    </div>


    <transition name="fade">
      <nav
        class="side-menu d-md-none"
        v-if="show_mobile_menu"
        @click.self="show_mobile_menu = false"
      >
        <div class="side-menu__container">
          <button
            class="side-menu__close"
            :aria-label="t('literal.close')"
            @click="show_mobile_menu = false"
          >
            <fa-icon
              aria-hidden="true"
              :icon="['fal', 'xmark-large']"
              fixed-width
            />
          </button>

          <div class="side-menu__wrapper">
            <ul
              class="side-menu__ul"
              v-if="navigation.length"
            >
              <li
                v-for="({ link }, index) in navigation"
                :key="`mobile-menu_${index}`"
                class="side-menu__li"
              >
                <app-link
                  :link="link"
                  class="side-menu__a link--naked link__color--white bold"
                />
              </li>
            </ul>

            <ul
              class="mobile-submenu"
              v-if="topnav.navigation?.length"
            >
              <li
                v-for="({ link }, index) in topnav.navigation"
                :key="`mobile-submenu_${index}`"
                class="side-menu__li"
              >
                <app-link
                  :link="link"
                  class="side-menu__a link--naked link__color--white bold"
                />
              </li>
            </ul>

            <language-switcher v-slot="{ options, updateLanguage, isActive }">
              <ul class="language__ul">
                <li
                  class="language__li"
                  v-for="language in options"
                  :key="language.id"
                >
                  <button
                    class="language__btn bold"
                    :class="{ 'language__btn--active': isActive(language.id) }"
                    type="button"
                    @click.prevent="updateLanguage(language.id)"
                  >
                    {{ language.label.value.toUpperCase() }}
                  </button>
                </li>
              </ul>
            </language-switcher>

            <div class="d-flex justify-content-center margin__t--24">
              <app-session-button
                class="color--white bold"
                @click="show_mobile_menu = false"
                @close="show_mobile_menu = false"
              />
            </div>
          </div>
        </div>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { ref, defineAsyncComponent, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from '@/vendors/i18n'
import { scrollTo } from '@/utils/utils'

import AppLogo from '&/atoms/AppLogo'
import LanguageSwitcher from '&/atoms/LanguageSwitcher'
import AppSessionButton from '&/organisms/AppSessionButton'
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppBurgerMenu = defineAsyncComponent(() => import('&/atoms/AppBurgerMenu'))

const { section } = defineProps({
  section: { type: Object, required: true }
})

const { t } = useI18n()
const route = useRoute()

const { topnav = {}, logo = {}, cta = {}, navigation = [] } = section.attributes

const show_mobile_menu = ref(false)

const toggleMobileMenu = () => {
  scrollTo()

  setTimeout(() => show_mobile_menu.value = !show_mobile_menu.value, 100)
}

watch(route, () => show_mobile_menu.value = false)
</script>

<style scoped lang="scss">
.header {
  font-family: $font-family--secondary;
  position: sticky;
  top: 0;
  z-index: 50;

  &__container {
    position: relative;
    display: flex;
    align-items: flex-start;

    &::before {
      content: '';
      width: 100vw;
      height: 64px;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      background: $white;
      z-index: -1;

      @include mq(md) {
        height: 90px;
      }
    }
  }
}

.topnav {
  background: #FAF8F8;
  color: #737476;
  font-size: rem(13px);
  line-height: 1.8461538462;

  &__ul {
    padding-top: 15px;
    padding-bottom: 13px;
  }

  &__li {
    &:not(.topnav__li--switcher) {
      padding: 0 18px;
    }

    &--switcher {
      padding: 0 6px;
    }

    &:empty {
      display: none;
    }

    &:not(:last-child) {
      border-right: 1px solid #BDBDBD;
    }
  }

  :deep() {
    .topnav {
      &__a {
        color: #737476;

        &:hover,
        &.is-active {
          color: $color--primary;
        }
      }
    }
  }
}

.language-switcher {
  &:deep() {
    .v-select {
      border: 0;
      text-align: center;
      font-family: $font-family--primary;

      .vs__dropdown-toggle {
        color: #737476;
        padding-block: 3px;
      }

      .vs__dropdown-menu {
        background: #FAF8F8;
        min-width: 24px;
      }

      .vs__dropdown-option {
        text-align: center;
        font-family: $font-family--primary;
        font-size: rem(12px);
        padding: 6px;
      }

      .vs__selected {
        font-size: rem(13px);
      }
    }

    .locale-switcher {
      background: none;
    }
  }
}

:deep() {
  .logo {
    &__wrapper {
      padding: 10px;
      background: $white;
      display: block;
      top: -10px;
      z-index: 20;
      flex-shrink: 1;
    }

    &__img {
      max-width: 55px;
      width: 100%;
      height: auto;

      @include mq(md) {
        max-width: 83px;
      }
    }
  }
}

.navigation {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 9px 0 11px;
  position: relative;
  flex: 1 1 auto;
  gap: 12px;

  @include mq(md) {
    gap: 24px;
    padding: 30px 0 16px;
  }

  @include mq(lg) {
    gap: 40px;
  }

  &__ul {
    align-items: center;
    gap: 24px;

    @include mq(lg) {
      gap: 55px;
    }
  }

  :deep() {
    .navigation {
      &__a {
        font-size: rem(15px);
        line-height: 1.3333333333;

        &.is-active {
          color: $color--primary;
        }
      }
    }
  }
}

:deep() {
  .cta {
    border-radius: 3px;
    font-size: rem(13px);
    white-space: nowrap;
    padding-inline: 12px;
    padding-block: 10px;
    line-height: 1.46;

    @include mq(sm) {
      padding-inline: 24px;
    }

    @include mq(md) {
      font-size: rem(15px);
    }
  }
}

.burger {
  padding-inline: 0!important;

  &:deep() {
    .menu {
      &__label {
        display: none;
      }

      &__line {
        background: $color--primary;
      }
    }
  }
}

.side-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 99;
  font-size: rem(13px);
  transition: all .3s ease-in-out;

  &.fade-enter-to,
  &.fade-enter-active {
    transform: translateX(0);
  }

  &.fade-enter-from,
  &.fade-leave-to {
    opacity: 0;
    transform: translateX(100%);
  }

  &__container {
    position: absolute;
    top: 0;
    right: 0;
    width: min(260px, 100%);
    height: 100%;
    background: #333;
  }

  &__wrapper {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    padding: 24px 0;
    overflow-y: auto;
  }

  &__close {
    background: #333;
    color: $white;
    position: absolute;
    left: 0;
    top: 12px;
    transform: translateX(-100%);
    transition: all 0.3s ease-in-out;
    padding: 12px;
    font-size: rem(14px);

    &:hover {
      background: $color--primary;
    }
  }

  :deep() {
    .side-menu {
      &__a {
        display: block;
        padding: 8px 18px;
        font-size: rem(13px);

        &:hover,
        &.is-active {
          color: $white;
          background: $color--primary;
        }
      }
    }

    .locale-switcher {
      background: none;
      width: 100%;
    }
  }
}

.mobile-submenu {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(#fff, 0.1);
}

.language {
  &__ul {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(#fff, 0.1);
    width: 100%;
  }

  &__btn {
    background: transparent;
    color: $white;
    padding: 12px;
    font-size: rem(12px);

    &--active {
      background: $color--primary;
    }
  }
}
</style>